/**
 * @fileoverview View mode content for user details modal
 */
import React from "react";
import PropTypes from "prop-types";
import { intl, DmsPermissions } from "dmsbasereact";
import { Box, Typography, Grid, Chip, List, ListItem, ListItemText, Paper } from "@mui/material";
import { getBrazilianStateLabel } from "../../constants/BrazilianStates";
import { Permissions } from "../../constants/Permissions";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import { DmsInfoCard } from "../DmsDetailModal/DmsInfoCard";
import { DmsInfoField } from "../DmsDetailModal/DmsInfoField";
import { UserDocuments } from "./UserDocuments";
import { formatCpf, formatCep, formatCnpj } from "../../utils/formatters";

/**
 * View mode content for displaying user details
 * Receives `data` prop from DmsDetailModal
 */
export const ViewModeContent = ({ data }) => {
  const user = data;
  

  const getAccountStatusColor = (status) => {
    // 5 = Active, 6 = Rejected, 7 = Suspended
    if (status === 5) return "success";
    if (status === 6 || status === 7) return "error";
    return "warning";
  };

  const getInternalStatusChip = () => {
    if (user.is_blocked) {
      return <Chip label={intl.get("Blocked").d("Blocked")} color="error" size="small" />;
    }
    if (user.status === 1) {
      return <Chip label={intl.get("Active").d("Active")} color="success" size="small" />;
    }
    return <Chip label={intl.get("Inactive").d("Inactive")} color="default" size="small" />;
  };

  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
      {/* Basic Information */}
      <DmsInfoCard icon={<PersonIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
        <Grid container spacing={2}>
          <DmsInfoField label={intl.get("Name").d("Name")} value={user.user_name} />
          <DmsInfoField label={intl.get("Email").d("Email")} value={user.user_email} />
          <Grid item xs={6} sm={4}>
            <Typography variant="body2" color="text.secondary">{intl.get("Status").d("Status")}</Typography>
            <Box sx={{ mt: 0.5 }}>
              {user.is_blocked ? (
                <Chip label={intl.get("Blocked").d("Blocked")} color="error" size="small" />
              ) : user.is_external && user.person?.account_status_label ? (
                <Chip
                  label={user.person.account_status_label}
                  size="small"
                  color={getAccountStatusColor(user.person.account_status)}
                />
              ) : (
                getInternalStatusChip()
              )}
            </Box>
          </Grid>
          {user.is_blocked && user.block_reason && (
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">
                {intl.get("BlockReason").d("Reason for Blocking")}
              </Typography>
              <Typography variant="body1">{user.block_reason}</Typography>
            </Grid>
          )}
          {!user.is_blocked && user.is_external && user.person?.account_status === 6 && user.rejection_reason && (
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">
                {intl.get("RejectionReason").d("Rejection Reason")}
              </Typography>
              <Typography variant="body1">{user.rejection_reason}</Typography>
            </Grid>
          )}
          {!user.is_blocked && user.is_external && user.person?.status_reason && user.person?.account_status !== 6 && (
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">
                {intl.get("StatusReason").d("Status Reason")}
              </Typography>
              <Typography variant="body1">{user.person.status_reason}</Typography>
            </Grid>
          )}
        </Grid>
      </DmsInfoCard>

      {/* Roles */}
      <DmsInfoCard icon={<SecurityIcon />} title={intl.get("Roles").d("Roles")}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {(user.roles || []).map((role, idx) => (
            <Chip key={idx} label={intl.get(`RoleName.${role.role_name}`).d(role.role_name)} variant="outlined" />
          ))}
          {(!user.roles || user.roles.length === 0) && (
            <Typography variant="body2" color="text.secondary">
              {intl.get("NoRolesAssigned").d("No roles assigned")}
            </Typography>
          )}
        </Box>
      </DmsInfoCard>

      {/* External user specific sections */}
      {user.is_external && (
        <>
          {/* Person Information */}
          {user.person && (
            <DmsInfoCard icon={<PersonIcon />} title={intl.get("PersonInfo").d("Person Information")}>
              <Grid container spacing={2}>
                <DmsInfoField label={intl.get("CPF").d("CPF")} value={formatCpf(user.person.cpf)} />
              </Grid>
            </DmsInfoCard>
          )}

          {/* Doctor Information */}
          {user.doctor && (
            <DmsInfoCard icon={<LocalHospitalIcon />} title={intl.get("DoctorInfo").d("Doctor Information")}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
                {intl.get("CRMRegistrations").d("CRM Registrations")}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Primary CRM */}
                {user.doctor.crm && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: "grey.50",
                      borderLeft: 3,
                      borderLeftColor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {`${user.doctor.crm} (${getBrazilianStateLabel(user.doctor.crm_state)})`}
                    </Typography>
                    <Chip
                      label={intl.get("MainCRM").d("Main CRM")}
                      color="success"
                      size="small"
                      variant="filled"
                    />
                  </Paper>
                )}
                {/* Additional CRMs */}
                {(user.doctor.additional_crms || []).map((item) => (
                  <Paper
                    key={`${item.crm}-${item.crm_state}`}
                    variant="outlined"
                    sx={{ p: 1.5, bgcolor: "grey.50", borderLeft: 3, borderLeftColor: "primary.main" }}
                  >
                    <Typography variant="body1">
                      {`${item.crm} (${getBrazilianStateLabel(item.crm_state)})`}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </DmsInfoCard>
          )}

          {/* Pharmacist Information */}
          {user.pharmacist && (
            <DmsInfoCard icon={<LocalPharmacyIcon />} title={intl.get("PharmacistInfo").d("Pharmacist Information")}>
              <Grid container spacing={2}>
                <DmsInfoField label={intl.get("CRF").d("CRF")} value={user.pharmacist.crf} />
                <DmsInfoField label={intl.get("CRFState").d("CRF State")} value={user.pharmacist.crf_state} />
                {user.institutions && user.institutions.length > 0 && (
                  <DmsInfoField
                    label={intl.get("Institution").d("Institution")}
                    value={user.institutions.map(inst => inst.name).join(", ")}
                    xs={12}
                  />
                )}
              </Grid>
            </DmsInfoCard>
          )}

          {/* Address Information */}
          {user.address && (
            <DmsInfoCard icon={<HomeIcon />} title={intl.get("AddressInfo").d("Address Information")}>
              <Grid container spacing={2}>
                <DmsInfoField label={intl.get("ZipCode").d("Zip Code")} value={formatCep(user.address.zip_code)} xs={4} />
                <DmsInfoField label={intl.get("Address").d("Address")} value={user.address.address} xs={8} />
                <DmsInfoField label={intl.get("Number").d("Number")} value={user.address.number} xs={4} />
                <DmsInfoField label={intl.get("City").d("City")} value={user.address.city} xs={4} />
                <DmsInfoField label={intl.get("State").d("State")} value={user.address.state} xs={4} />
              </Grid>
            </DmsInfoCard>
          )}

          {/* Institutions */}
          {user.institutions && user.institutions.length > 0 && (
            <DmsInfoCard icon={<BusinessIcon />} title={intl.get("Institutions").d("Institutions")}>
              <List dense>
                {user.institutions.map((inst, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={inst.name}
                      secondary={inst.cnpj ? `CNPJ: ${formatCnpj(inst.cnpj)}` : null}
                    />
                  </ListItem>
                ))}
              </List>
            </DmsInfoCard>
          )}

          {/* Documents - shown for pharmacists and persons (institution responsibles) */}
          {user.person && (user.pharmacist || !user.doctor) && (
            <DmsPermissions
              permissions={Permissions.USER_DOCUMENTS_UPLOAD}
              fallback={<UserDocuments personPk={user.person.pk} allowUpload={false} />}
            >
              <UserDocuments personPk={user.person.pk} allowUpload={true} />
            </DmsPermissions>
          )}
        </>
      )}
    </Box>
  );
};

ViewModeContent.propTypes = {
  data: PropTypes.object.isRequired,
};
